import * as React from 'react';
import { useSelector } from 'react-redux';

import type { AiCoach_RespondToUserMessageInput as AiCoachRespondToUserMessageInput } from '__generated__/graphql-types';

import { useLocation } from 'js/lib/useRouter';

import type { ButtonProps } from '@coursera/cds-core';
import type { CourseItem, Product } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachTriggerButton } from 'bundles/ai-coach-platform/components/building-blocks';
import type { Props as CoachTriggerButtonProps } from 'bundles/ai-coach-platform/components/building-blocks/CoachTriggerButton';
import useCoachTriggerMessage from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useCoachTriggerMessage';
import StoreProviderWrapper from 'bundles/ai-coach-platform/components/patterns/chat/store/StoreProviderWrapper';
import type { GlobalState } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import {
  type CoachTrackingData,
  CoachTrackingDataProvider,
  useCoachTrackingContext,
} from 'bundles/ai-coach-platform/utils/tracking';

export type ExternalCoachActionProps = {
  // TODO: Add prop for metadata from page to include in context to Coach e.g. recommended specialization
  onClick?: (e?: React.MouseEvent) => void;
  /**
   * The label to display on the button.
   *
   * This will be the default message that will be sent to Coach. To send a message that is different
   * from the displayed label, provide that in the `message` prop.
   * */
  label: string;

  /**
   * Whether a new session should be created when this action is clicked.
   * This is the same as starting a new Coach chat.
   */
  newSessionOnClick?: boolean;

  /**
   * Whether session state should be updated before dispatching the action to redux
   *
   * @default false
   */
  skipSessionUpdate?: boolean;

  /**
   * Whether this action button is used within the CoachDisclosure component
   * @default false
   */
  isDisclosureButton?: boolean;

  /**
   * Whether to use text streaming for chat messages.
   */
  enableTextStreaming?: boolean;

  /**
   * Whether to enable the continuous version of Coach chat
   *
   * @default false
   */
  enableContinuousChat?: boolean;

  /**
   * A custom Coach trigger button component to be used instead of the default one
   */
  customCoachTriggerButton?: React.ComponentType<CoachTriggerButtonProps>;

  /**
   * Whether to enable logged out mode for Coach chat.
   * When enabled, the eventing device UUID will be passed to GraphQL queries for logged out Coach.
   *
   * Note: This only works when enableTextStreaming is also true, as the device ID is only added
   * for the subscriptions path in the dispatcher.
   *
   * @default false
   */
  enableLoggedOutMode?: boolean;

  trackingData?: CoachTrackingData;
  /** Optional content override — when provided, renders inside the button instead of `label`. */
  children?: React.ReactNode;
} & Omit<ButtonProps, 'children'> &
  AiCoachRespondToUserMessageInput;

export const ExternalCoachActionContent = (props: ExternalCoachActionProps): React.ReactElement => {
  const {
    additionalContext,
    config,
    courseraContext,
    variant,
    onClick,
    label,
    message,
    newSessionOnClick,
    className,
    responseProcessingType,
    isDisclosureButton = false,
    skipSessionUpdate = false,
    enableTextStreaming = false,
    enableContinuousChat = false,
    customCoachTriggerButton,
    enableLoggedOutMode = false,
    children,
    ...rest
  } = props;

  const track = useTracker();
  const location = useLocation();
  const trackingData = useCoachTrackingContext()?.data;

  const { isMessageLoading, isMessageStreaming } = useSelector((state: GlobalState) => {
    return {
      isMessageLoading: state.behavior.messageLoader,
      isMessageStreaming: state.behavior.isMessageStreaming,
    };
  });

  const onCoachActionClick = useCoachTriggerMessage({
    newSessionOnClick,
    onClick,
    skipSessionUpdate,
    enableContinuousChat,
    enableTextStreaming,
    enableLoggedOutMode,
    additionalContext,
    courseraContext,
    config,
    responseProcessingType,
    location,
  });

  const trackingLocation = isDisclosureButton ? 'disclosure' : trackingData?.location;

  const TriggerButtonComponent = customCoachTriggerButton ?? CoachTriggerButton;

  return (
    <TriggerButtonComponent
      disabled={isMessageLoading || isMessageStreaming}
      variant={variant}
      onClick={(e?: React.MouseEvent<HTMLButtonElement>) => {
        onCoachActionClick({ message, label });

        if (config.coachModeId && config.coachActionId) {
          track('interact_coach', {
            coachInteractionType: 'click_action',
            coach: {
              mode: config.coachModeId,
              ...(trackingLocation && { location: trackingLocation }),
            },
            ...(courseraContext?.itemId && {
              courseItem: {
                id: courseraContext.itemId,
              } as CourseItem,
            }),
            ...(courseraContext?.courseId && {
              product: {
                type: 'course',
                id: courseraContext.courseId,
              } as Product,
            }),
            coachAction: config.coachActionId,
          });
        }
      }}
      className={className}
      {...rest}
    >
      {children ?? label}
    </TriggerButtonComponent>
  );
};

export const ExternalCoachAction = (props: ExternalCoachActionProps): React.ReactElement => {
  return (
    <StoreProviderWrapper>
      <CoachTrackingDataProvider
        value={{
          data: { mode: props.config.coachModeId || 'UNKNOWN_UNDEFINED', location: props.trackingData?.data.location },
        }}
      >
        <ExternalCoachActionContent {...props} />
      </CoachTrackingDataProvider>
    </StoreProviderWrapper>
  );
};

export default ExternalCoachAction;
